import { Avatar, Menu, MenuButton, MenuDivider, MenuItem, MenuList, Text, HStack, Box } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthProvider';

/**
 * Header-right dropdown shown when a user is signed in. Avatar + email;
 * click opens Favourites / Credits / Sign out (mirrors the shape used
 * on pictools / hfn — anything account-related lives behind this menu
 * rather than as separate top-level nav buttons).
 */
export function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const email: string = user?.email || '';
  const initial = (email[0] || 'U').toUpperCase();

  return (
    <Menu placement="bottom-end">
      <MenuButton
        aria-label="User menu"
        data-cta="user:menu-open"
        _hover={{ opacity: 0.85 }}
        _focus={{ outline: '2px solid', outlineColor: 'accent.500', outlineOffset: '2px' }}
        borderRadius="full"
      >
        <Avatar size="sm" name={initial} bg="accent.500" color="white" />
      </MenuButton>
      <MenuList bg="night.800" borderColor="whiteAlpha.300" py={1} minW="220px">
        <Box px={3} py={2}>
          <Text fontSize="xs" color="whiteAlpha.600">
            {t('userMenu.signedInAs', 'Signed in as')}
          </Text>
          <Text fontSize="sm" color="whiteAlpha.900" noOfLines={1}>
            {email || t('userMenu.anonymous', 'User')}
          </Text>
        </Box>
        <MenuDivider borderColor="whiteAlpha.200" />
        <MenuItem
          bg="night.800"
          color="whiteAlpha.900"
          _hover={{ bg: 'whiteAlpha.200' }}
          _focus={{ bg: 'whiteAlpha.200' }}
          onClick={() => navigate('/songs')}
        >
          {t('userMenu.mySongs', 'My songs')}
        </MenuItem>
        <MenuItem
          bg="night.800"
          color="whiteAlpha.900"
          _hover={{ bg: 'whiteAlpha.200' }}
          _focus={{ bg: 'whiteAlpha.200' }}
          onClick={() => navigate('/library')}
        >
          {t('userMenu.library', 'Music library')}
        </MenuItem>
        <MenuItem
          bg="night.800"
          color="whiteAlpha.900"
          _hover={{ bg: 'whiteAlpha.200' }}
          _focus={{ bg: 'whiteAlpha.200' }}
          onClick={() => navigate('/favourites')}
        >
          {t('userMenu.favourites', 'Favourites')}
        </MenuItem>
        <MenuItem
          bg="night.800"
          color="whiteAlpha.900"
          _hover={{ bg: 'whiteAlpha.200' }}
          _focus={{ bg: 'whiteAlpha.200' }}
          onClick={() => navigate('/credits')}
        >
          {t('userMenu.credits', 'Credits')}
        </MenuItem>
        <MenuDivider borderColor="whiteAlpha.200" />
        <MenuItem
          bg="night.800"
          color="whiteAlpha.900"
          _hover={{ bg: 'whiteAlpha.200' }}
          _focus={{ bg: 'whiteAlpha.200' }}
          onClick={async () => {
            await logout();
            navigate('/');
          }}
        >
          {t('userMenu.signOut', 'Sign out')}
        </MenuItem>
      </MenuList>
    </Menu>
  );
}
